import { z } from 'zod';
import { ZOHO_MODULES, type ZohoModuleName } from '../zoho/fields.js';
import { isCustomModuleKey, type ZohoModuleKey } from '../zoho/backup.js';

/**
 * The Zoho migration contract.
 *
 * Three steps, and the middle one is the reason the other two are shaped as
 * they are:
 *
 *  1. **analyze** — what is in this bundle? Which modules, which columns, what
 *     did the auto-mapping do, and what could it not place.
 *  2. **dry run** — what would happen? Every row coerced and validated, every
 *     lookup resolved, counted per module. Nothing written.
 *  3. **commit** — do it, one transaction per module, in dependency order.
 *
 * The dry run runs the identical pipeline as the commit, so what it promises
 * is what the commit does. A preview that validates loosely and a commit that
 * writes strictly is worse than no preview: it converts "1,000 rows ready"
 * into "940 imported" with no explanation of the missing 60.
 */

export const ZOHO_MODULE_NAMES = ZOHO_MODULES.map((module) => module.module) as [
  ZohoModuleName,
  ...ZohoModuleName[],
];

/**
 * Bounds. A Zoho backup of a mid-sized org is tens of megabytes; these keep one
 * upload from exhausting the process while staying well clear of a real export.
 */
export const MAX_ZOHO_BUNDLE_BYTES = 64 * 1024 * 1024;
export const MAX_ZOHO_ROWS_PER_MODULE = 100_000;
/** Issues reported per module. Beyond this the file is wrong, not the rows. */
export const MAX_ZOHO_ISSUES = 200;

/** One module's file inside a bundle. */
/**
 * A built-in module name, or `custom:<apiName>` for one of the org's own
 * modules. Custom modules are not in the dictionary: their structure is read
 * from the file's own header (and the Metadata zip when it was uploaded).
 */
// A refined string rather than `z.custom`: the API documents these schemas as
// JSON Schema, and a custom type cannot be represented there, which stops the
// server from booting.
export const zohoModuleKeySchema = z
  .string()
  .max(60)
  .refine(
    (value) =>
      (ZOHO_MODULE_NAMES as readonly string[]).includes(value) ||
      (isCustomModuleKey(value) && value.length > 'custom:'.length),
    'Unknown module',
  ) as unknown as z.ZodType<ZohoModuleKey, string>;

export const zohoFileSchema = z.object({
  /** The filename as it appeared in the export, for the UI to echo back. */
  name: z.string().min(1),
  /** Which module this file holds. Detected from the name, overridable. */
  module: zohoModuleKeySchema,
  /** The display name of a custom module, taken from its filename or metadata. */
  label: z.string().min(1).max(80).optional(),
  csv: z.string().min(1),
});
export type ZohoFile = z.infer<typeof zohoFileSchema>;

/**
 * Per-module column overrides: Zoho header → WHNCMS field name, or the empty
 * string to ignore a column the auto-mapper claimed.
 */
// `partialRecord`, not `record`: overrides are supplied only for the modules
// the user actually touched. Zod's plain record over an enum key demands an
// entry per module, so the client could not send `{}`.
export const zohoMappingOverrideSchema = z.partialRecord(
  z.enum(ZOHO_MODULE_NAMES),
  z.record(z.string(), z.string()),
);
export type ZohoMappingOverride = z.infer<typeof zohoMappingOverrideSchema>;

export const zohoImportOptionsSchema = z.object({
  /**
   * Read `03/04/2026` as 4 March. Off by default because Zoho's own exports
   * are ISO; it exists for files that have been through Excel.
   */
  dayFirst: z.coerce.boolean().default(false),
  /**
   * Keep columns the dictionary does not know as custom fields, creating the
   * field definitions to match. On by default — the alternative is silently
   * dropping data the customer definitely cares about.
   */
  captureCustomFields: z.coerce.boolean().default(true),
  /**
   * Assign records whose Zoho business manager has no WHNCMS user to this user.
   * Omitted, they fall to whoever ran the import.
   */
  fallbackOwnerId: z.uuid().optional(),
});
export type ZohoImportOptions = z.infer<typeof zohoImportOptionsSchema>;

export const zohoAnalyzeSchema = z.object({
  files: z.array(zohoFileSchema).min(1, 'Upload at least one file'),
});
export type ZohoAnalyzeInput = z.infer<typeof zohoAnalyzeSchema>;

/**
 * What the Metadata zip said, in the compact form the later steps need. Sent
 * back by the browser alongside the files, like the files themselves.
 */
export const zohoMetadataSchema = z.object({
  modules: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      apiName: z.string(),
      singularLabel: z.string(),
      type: z.string(),
    }),
  ),
  customModules: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      apiName: z.string(),
      singularLabel: z.string(),
      type: z.string(),
    }),
  ),
  fields: z.array(
    z.object({
      id: z.string(),
      moduleId: z.string(),
      label: z.string(),
      apiName: z.string(),
      dataType: z.string(),
      isCustom: z.boolean(),
      maxLength: z.number().nullable(),
      options: z.array(z.string()),
    }),
  ),
  roles: z.number(),
  profiles: z.number(),
  orgName: z.string().nullable(),
});

export const zohoDryRunSchema = z.object({
  files: z.array(zohoFileSchema).min(1),
  mapping: zohoMappingOverrideSchema.optional(),
  options: zohoImportOptionsSchema.optional(),
  metadata: zohoMetadataSchema.optional(),
  /** Modules to include. Omitted, every module in the bundle. */
  modules: z.array(zohoModuleKeySchema).optional(),
});
export type ZohoDryRunInput = z.infer<typeof zohoDryRunSchema>;

export const zohoCommitSchema = zohoDryRunSchema;
export type ZohoCommitInput = z.infer<typeof zohoCommitSchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export interface ZohoColumnMapping {
  /** For a custom module: the type the field will be created with. */
  type?: string;
  header: string;
  /** The WHNCMS field, or null when the column is unmapped. */
  field: string | null;
  label: string;
  required: boolean;
  /** Set when WHNCMS recomputes this rather than importing the value. */
  derived: boolean;
  /** Set for a lookup: the module whose Record Id this column holds. */
  lookupModule: ZohoModuleName | null;
}

export interface ZohoModuleAnalysis {
  module: ZohoModuleKey;
  /** True for one of the org's own modules, created on import if absent. */
  custom?: boolean;
  label: string;
  fileName: string;
  totalRows: number;
  columns: ZohoColumnMapping[];
  /** Columns the dictionary does not know; offered as custom fields. */
  unmappedHeaders: string[];
  /** Required fields the file does not supply. A module with any of these cannot import. */
  missingRequired: string[];
  /** Whether this module carries a Zoho Record Id column at all. */
  hasRecordIds: boolean;
}

export interface ZohoAnalysis {
  modules: ZohoModuleAnalysis[];
  /** Files whose module could not be determined from the name. */
  unrecognizedFiles: string[];
  /** Files recognised but deliberately not imported, with the reason. */
  skippedFiles?: { name: string; reason: string }[];
  /** What the Metadata zip contributed, when it was uploaded. */
  metadata?: {
    orgName: string | null;
    modules: number;
    customModules: number;
    fields: number;
  } | null;
  /** Import order, so the UI can show it and explain why. */
  importOrder: ZohoModuleKey[];
}

export interface ZohoRowIssue {
  /** 1-based, counting the header as row 1 — what a spreadsheet shows. */
  row: number;
  field: string;
  message: string;
}

/** A lookup that pointed at a record not present in the bundle. */
export interface ZohoUnresolvedLink {
  field: string;
  /** A Zoho CRM module, or a Zoho Books entity. */
  targetModule: string;
  count: number;
  /** A few of the Zoho ids that did not resolve, to make the cause findable. */
  examples: string[];
}

export interface ZohoModuleResult {
  /** A Zoho CRM module key, or a Zoho Books entity name. */
  module: string;
  label: string;
  totalRows: number;
  /** Rows that would be, or were, created. */
  created: number;
  /** Rows matched to an existing record by Zoho Id and updated instead. */
  updated: number;
  /** Rows skipped as duplicates within this import set. */
  skipped: number;
  /** Rows that could not be imported at all. */
  failed: number;
  issues: ZohoRowIssue[];
  unresolvedLinks: ZohoUnresolvedLink[];
  /** Custom field definitions this module would create, or created. */
  customFields: string[];
  /** Set when the whole module was refused — the reason is in `error`. */
  error?: string;
}

export interface ZohoImportReport {
  /** True when nothing was written. */
  dryRun: boolean;
  modules: ZohoModuleResult[];
  /** Sample rows per module, so the mapping can be eyeballed before committing. */
  samples: Record<string, Record<string, unknown>[]>;
}

/** The export side: the same shape, going out. */
export const zohoExportSchema = z.object({
  modules: z.array(z.enum(ZOHO_MODULE_NAMES)).min(1),
  /**
   * Emit Zoho's API names (`Last_Name`) rather than its display labels
   * (`Last Name`). Labels by default, because that is what a Zoho CSV export
   * contains and therefore what a round trip should produce.
   */
  apiNames: z.coerce.boolean().default(false),
});
export type ZohoExportInput = z.infer<typeof zohoExportSchema>;

// ---------------------------------------------------------------------------
// Attachments (the PDFs and images in a backup)
// ---------------------------------------------------------------------------

/**
 * Attachments travel separately from the records: a backup's files can run to
 * hundreds of megabytes, so they are uploaded straight to the server and
 * streamed into storage, never round-tripped through the browser as JSON.
 */
export const MAX_ZOHO_ATTACHMENT_UPLOAD_BYTES = 512 * 1024 * 1024;
/** One file. Anything larger is reported and skipped rather than loaded into memory. */
export const MAX_ZOHO_ATTACHMENT_FILE_BYTES = 50 * 1024 * 1024;

export interface ZohoAttachmentReport {
  /** Rows in the backup's Attachments CSV. */
  totalRows: number;
  imported: number;
  /** Already imported by an earlier run (matched by Zoho Record Id). */
  alreadyImported: number;
  /** In the CSV, but no file for it was in the zips uploaded this time. */
  missingFile: number;
  /** Over the per-file limit. */
  tooLarge: number;
  /** A link or cloud document, not a file. */
  linkOnly: number;
  /** The record it hangs off was not imported (or is not a module that holds files). */
  parentMissing: number;
  /** The same, by the Books record type the file belongs to. */
  parentMissingByType?: Record<string, number>;
  /** Modules that cannot hold attachments here, with how many each. */
  unsupportedParents: Record<string, number>;
  /** Files in the zips that no CSV row accounted for. */
  unmatchedFiles: number;
  /** A few examples of the above, to make each findable. */
  examples: { kind: string; fileName: string; detail?: string }[];
  bytesImported: number;
}
